import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Edit3,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Info,
  AlertTriangle,
  AlertCircle,
  CheckCircle,
  X,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { AUTH_URL } from "../../constants";

const TYPE_CONFIG = {
  info: {
    label: "Info",
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
    icon: Info,
  },
  warning: {
    label: "Warning",
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
    icon: AlertTriangle,
  },
  error: {
    label: "Error",
    bg: "bg-red-50",
    text: "text-red-700",
    border: "border-red-200",
    icon: AlertCircle,
  },
  success: {
    label: "Success",
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
    icon: CheckCircle,
  },
};

const EMPTY_FORM = {
  content: "",
  message_type: "info",
  scope: "global",
  facility_id: "",
};

const SystemMessages = () => {
  const { token, isAdmin } = useAuth();
  const [messages, setMessages] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState(null); // null | "create" | "edit"
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${AUTH_URL}/admin/system-messages`, { headers });
      const data = await res.json();
      setMessages(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchFacilities = useCallback(async () => {
    try {
      const res = await fetch(`${AUTH_URL}/admin/facilities`, { headers });
      const data = await res.json();
      setFacilities(Array.isArray(data) ? data : []);
    } catch {
      // non-critical
    }
  }, [token]);

  useEffect(() => {
    fetchMessages();
    fetchFacilities();
  }, [fetchMessages, fetchFacilities]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setError("");
    setEditTarget(null);
    setModal("create");
  };

  const openEdit = (msg) => {
    setForm({
      content: msg.content,
      message_type: msg.message_type,
      scope: msg.scope,
      facility_id: msg.facility_id || "",
    });
    setError("");
    setEditTarget(msg);
    setModal("edit");
  };

  const closeModal = () => {
    setModal(null);
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const handleSave = async () => {
    if (!form.content.trim()) {
      setError("Message content is required.");
      return;
    }
    if (form.scope === "facility" && !form.facility_id) {
      setError("Select a facility for facility-scoped messages.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const body = {
        ...form,
        facility_id: form.scope === "global" ? null : form.facility_id,
      };
      const url =
        modal === "edit"
          ? `${AUTH_URL}/admin/system-messages/${editTarget.id}`
          : `${AUTH_URL}/admin/system-messages`;
      const res = await fetch(url, {
        method: modal === "edit" ? "PUT" : "POST",
        headers,
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.error || "Save failed");
        return;
      }
      closeModal();
      fetchMessages();
    } catch {
      setError("Network error");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (msg) => {
    await fetch(`${AUTH_URL}/admin/system-messages/${msg.id}`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ is_active: !msg.is_active }),
    });
    fetchMessages();
  };

  const handleDelete = async (msg) => {
    if (!window.confirm("Delete this message permanently?")) return;
    await fetch(`${AUTH_URL}/admin/system-messages/${msg.id}`, {
      method: "DELETE",
      headers,
    });
    fetchMessages();
  };

  const TypeBadge = ({ type }) => {
    const c = TYPE_CONFIG[type] || TYPE_CONFIG.info;
    const Icon = c.icon;
    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}
      >
        <Icon className="w-3 h-3 mr-1" />
        {c.label}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">System Messages</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Banners shown to users in the client portal — globally or per
            product.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" /> New Message
        </button>
      </div>

      {/* Preview examples */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-slate-500 uppercase">
          Preview styles
        </p>
        {Object.entries(TYPE_CONFIG).map(([type, c]) => {
          const Icon = c.icon;
          return (
            <div
              key={type}
              className={`flex items-start px-4 py-2.5 rounded-lg border ${c.bg} ${c.border}`}
            >
              <Icon className={`w-4 h-4 mt-0.5 mr-2 flex-shrink-0 ${c.text}`} />
              <span className={`text-sm ${c.text}`}>
                This is a sample <strong>{c.label}</strong> message.
              </span>
            </div>
          );
        })}
      </div>

      {/* Messages table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Message
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Type
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Scope
              </th>
              <th className="text-left px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Status
              </th>
              <th className="text-right px-4 py-3 text-xs font-medium text-slate-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  Loading...
                </td>
              </tr>
            ) : messages.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-8 text-center text-slate-400"
                >
                  No system messages yet.
                </td>
              </tr>
            ) : (
              messages.map((msg) => (
                <tr
                  key={msg.id}
                  className={`border-b border-slate-100 ${!msg.is_active ? "opacity-50" : ""}`}
                >
                  <td className="px-4 py-3 max-w-md">
                    <p className="text-slate-800 truncate">{msg.content}</p>
                    {msg.created_by && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        by {msg.created_by}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <TypeBadge type={msg.message_type} />
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        msg.scope === "global"
                          ? "bg-slate-100 text-slate-600"
                          : "bg-violet-100 text-violet-700"
                      }`}
                    >
                      {msg.scope === "global" ? "Global" : msg.facility_id}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggle(msg)}
                      className="flex items-center text-xs"
                    >
                      {msg.is_active ? (
                        <ToggleRight className="w-5 h-5 text-green-500 mr-1" />
                      ) : (
                        <ToggleLeft className="w-5 h-5 text-slate-400 mr-1" />
                      )}
                      <span
                        className={
                          msg.is_active ? "text-green-600" : "text-slate-400"
                        }
                      >
                        {msg.is_active ? "Active" : "Inactive"}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end space-x-1">
                      <button
                        onClick={() => openEdit(msg)}
                        className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Edit"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      {isAdmin && (
                        <button
                          onClick={() => handleDelete(msg)}
                          className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-800">
                {modal === "create"
                  ? "New System Message"
                  : "Edit System Message"}
              </h3>
              <button
                onClick={closeModal}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Message *
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, content: e.target.value }))
                  }
                  rows={3}
                  placeholder="Enter the message to display to users..."
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Type
                  </label>
                  <select
                    value={form.message_type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, message_type: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {Object.entries(TYPE_CONFIG).map(([v, c]) => (
                      <option key={v} value={v}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Scope
                  </label>
                  <select
                    value={form.scope}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        scope: e.target.value,
                        facility_id: "",
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="global">Global (all users, header)</option>
                    <option value="facility">
                      Facility (product page only)
                    </option>
                  </select>
                </div>
              </div>

              {form.scope === "facility" && (
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Facility *
                  </label>
                  <select
                    value={form.facility_id}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, facility_id: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select facility…</option>
                    {facilities.map((f) => (
                      <option key={f.facility_id} value={f.facility_id}>
                        {f.display_name || f.facility_id}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Live preview */}
              {form.content.trim() &&
                (() => {
                  const c = TYPE_CONFIG[form.message_type] || TYPE_CONFIG.info;
                  const Icon = c.icon;
                  return (
                    <div
                      className={`flex items-start px-4 py-2.5 rounded-lg border ${c.bg} ${c.border}`}
                    >
                      <Icon
                        className={`w-4 h-4 mt-0.5 mr-2 flex-shrink-0 ${c.text}`}
                      />
                      <span className={`text-sm ${c.text}`}>
                        {form.content}
                      </span>
                    </div>
                  );
                })()}

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex justify-end space-x-3 px-5 py-4 border-t border-slate-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                {saving
                  ? "Saving…"
                  : modal === "create"
                    ? "Create"
                    : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemMessages;
